// Tiny event bus between the app and Bit, the floating learning robot.
// Pages report what happened; BitCompanion decides how Bit reacts.

/**
 * @param {"success"|"fail"|"compile-error"|"lesson-done"|"quiz"|"hint"|"task-checking"|"task-solved"} type
 * @param {{text?: string, context?: string, xp?: number, task?: any, code?: string, language?: string, lessonTitle?: string, message?: string}} [detail]
 */
export function bitEvent(type, detail = {}) {
  window.dispatchEvent(new CustomEvent("bit-event", { detail: { type, ...detail } }));
}

/** Opens Bit's chat, optionally with code/lesson context and a prefilled question. */
export function openBit({ context, message } = {}) {
  window.dispatchEvent(new CustomEvent("open-tutor", { detail: { context, message } }));
}

/**
 * Erstellt eine motivierende, auf die Aufgabe zugeschnittene Zusammenfassung und ein Lob von Bit.
 */
export function buildTaskSummaryAndPraise({ task, code = "", language = "", lessonTitle = "", xp = 0 } = {}) {
  const instructions = (typeof task === "string" ? task : task?.instructions || "").trim();
  const lower = instructions.toLowerCase();

  // Themen-Schwerpunkt erkennen
  let focus = "";
  if (lower.includes("print")) {
    focus = "Textausgabe auf der Konsole mit `print()`";
  } else if (lower.includes("variable") || lower.includes("f-string")) {
    focus = "Variablen & formatierte Zeichenketten";
  } else if (lower.includes("if") || lower.includes("else")) {
    focus = "Bedingte Logik & Verzweigungen (`if` / `else`)";
  } else if (lower.includes("for") || lower.includes("while") || lower.includes("range") || lower.includes("schleife")) {
    focus = "Schleifen & wiederholte Ausführung";
  } else if (lower.includes("funktion") || lower.includes("def ")) {
    focus = "Funktionsdefinitionen & Rückgabewerte";
  } else if (lower.includes("klasse") || lower.includes("class") || lower.includes("__init__")) {
    focus = "Objektorientierte Programmierung & Klassen";
  } else if (lower.includes("comprehension")) {
    focus = "List Comprehensions";
  } else if (lower.includes("try") || lower.includes("except")) {
    focus = "Fehlerbehandlung (`try` / `except`)";
  } else if (lower.includes("async") || lower.includes("await")) {
    focus = "Asynchrone Programmierung";
  } else if (lower.includes("array") || lower.includes("numpy")) {
    focus = "Arrays & numerische Datenverarbeitung";
  } else if (lower.includes("css") || lower.includes("style") || lower.includes("background")) {
    focus = "CSS-Gestaltung & Styling";
  } else if (lower.includes("html") || lower.includes("tag")) {
    focus = "HTML-Struktur & Markup";
  }

  // Abwechslungsreiches Lob
  const praises = [
    "Fantastisch gelöst! 🎉",
    "Hervorragend gemacht! 🌟",
    "Saubere Arbeit! 🚀",
    "Genau so wird's gemacht! 👏",
    "Treffer! Perfekt umgesetzt! 💡",
    "Richtig stark! Die Leiterbahn leuchtet! ⚡",
  ];
  const praise = praises[Math.floor(Math.random() * praises.length)];

  let summarySection = "";
  if (instructions) {
    summarySection = `**Aufgabe zusammengefasst:**\n> *${instructions}*`;
  } else if (lessonTitle) {
    summarySection = `Du hast die Programmieraufgabe zur Lektion **„${lessonTitle}“** erfolgreich gemeistert.`;
  } else {
    summarySection = "Du hast die gestellte Programmieraufgabe erfolgreich gelöst.";
  }

  const focusSection = focus ? `\n\n🎯 **Schwerpunkt:** ${focus}` : "";
  const xpSection = xp > 0 ? `\n\n🏆 **+${xp} XP** gutgeschrieben!` : "";

  const markdown = `### ${praise}

${summarySection}${focusSection}

**Klasse Leistung:** Dein Code wurde geprüft und erfüllt die Anforderung fehlerfrei. Du hast das Konzept verstanden und direkt richtig angewendet. Weiter so! 💪${xpSection}`;

  const cleanInst = instructions
    .replace(/[`*_#>]/g, "")
    .replace(/print\(\)/g, "print")
    .replace(/\bdef\b/g, "def")
    .slice(0, 160);
  const cleanPraise = praise.replace(/[^\w\säöüÄÖÜß!]/g, "").trim();

  const speakText = `${cleanPraise}! Du hast die Aufgabe erfolgreich gemeistert. ${cleanInst ? `Hier ging es darum: ${cleanInst}.` : ""} Dein Code funktioniert einwandfrei. Sehr gut gemacht!`;

  return { markdown, speakText, praise, instructions, focus, xp };
}
